import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import React, { useMemo } from 'react';

import { CinemaRoomDto } from '@cineamo/api/models/cinema-room/CinemaRoomDto.types';
import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { getMovieAgeRating, MovieVersionDto } from '@cineamo/api/helpers/movie-helper';

import { useGetAgeRatingForISO_3166_2 } from '@cineamo/ui/src/helpers/age-rating-helper';
import { getISO_3166_2 } from '@cineamo/ui/src/helpers/cinema-helper';
import { cn } from '@cineamo/ui/src/helpers/cn';
import { useAgeRatingText } from '@cineamo/ui/src/helpers/movie-helper';
import {
    getShowingName,
    getShowingTicketLanguages,
    getShowingPropertiesDimension,
    getShowingTechnologies
} from '@cineamo/ui/src/helpers/showing-helper';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { IconAudio16 } from '@cineamo/ui/src/icons/ic-audio';
import { IconSubtitle16 } from '@cineamo/ui/src/icons/ic-subtitle';
import CinemaProfileImageRound from '@cineamo/ui/src/image/CinemaProfileImageRound';
import DotSeparatedLabel from '@cineamo/ui/src/label/DotSeparatedLabel';

import MoviePoster from '@cineamo/composables/src/ticket-booking/MoviePoster';

type ShowingDetailsProps = {
    showing: ShowingDto;
    movie?: MovieVersionDto;
    showCinemaName?: boolean;
    cinemaRoom?: CinemaRoomDto;
    /**
     * Given, the showing is one line (poster, title, date) with a link to this page, the film with its other
     * screenings; used above the seat map on small screens.
     */
    compactDetailsHref?: string;
    /** Given, "Details" in the compact line opens the showing's info here instead of leaving for the film. */
    onShowCompactDetails?: () => void;
};

const COMPACT_DETAILS_CLASS =
    'flex-none rounded-4 text-body-bold-12 text-[var(--secondary-color,var(--primary-color))] outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-color)]';

const ShowingDetails: React.FC<ShowingDetailsProps> = ({
    showing,
    movie,
    showCinemaName,
    cinemaRoom,
    compactDetailsHref,
    onShowCompactDetails
}) => {
    const cinema = showing._embedded?.cinema;
    const locale = useLocale();
    const formatter = useFormat();
    const t = useTranslations();
    const countryCode = showing._embedded?.cinema?.countryCode;

    const showingDateTime = useMemo(
        () => formatter.dateTime.formatDateTimeLong(showing.startDatetime),
        [formatter.dateTime, showing.startDatetime]
    );

    const showingName = getShowingName(showing, movie ?? undefined, locale, countryCode);
    const showingTechnologies = getShowingTechnologies(showing).map((technology: string) =>
        t(`showtimes.showing-info.technology.${technology}`)
    );
    const showingProperties = [getShowingPropertiesDimension(showing) ?? '2D', ...showingTechnologies];
    const tmdbAgeRating = getMovieAgeRating(movie ?? undefined, countryCode);
    const { ageRating: cinAgeRating } = useGetAgeRatingForISO_3166_2({
        movieId: movie?.id,
        iso31662: getISO_3166_2(cinema) ?? undefined,
        enabled: !tmdbAgeRating
    });
    const ageRating = tmdbAgeRating || cinAgeRating?.rating;
    const ageRatingText = useAgeRatingText(ageRating, countryCode);
    const {
        languageInfo: languageVersion,
        audioLanguage,
        subtitleLanguage
    } = getShowingTicketLanguages(showing, locale);
    const languageInfo =
        languageVersion || audioLanguage || subtitleLanguage ? (
            <span className="inline-flex items-center gap-6">
                {languageVersion}
                {audioLanguage && (
                    <span className="inline-flex items-center gap-4">
                        <IconAudio16 />
                        {audioLanguage}
                    </span>
                )}
                {subtitleLanguage && (
                    <span className="inline-flex items-center gap-4">
                        <IconSubtitle16 />
                        {subtitleLanguage}
                    </span>
                )}
            </span>
        ) : null;

    if (compactDetailsHref) {
        return (
            <div className="flex items-center gap-12 px-[9px] py-12 text-[var(--on-background-color)]">
                <div className="relative h-36 w-24 flex-none overflow-clip rounded-4">
                    <MoviePoster
                        movie={movie ?? undefined}
                        countryCode={countryCode}
                    />
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <h2 className="truncate text-body-bold-14">{showingName}</h2>
                    <p className="truncate text-body-10 font-montserrat-medium leading-[18px]">{showingDateTime}</p>
                </div>
                {onShowCompactDetails ? (
                    <button
                        type="button"
                        onClick={onShowCompactDetails}
                        className={COMPACT_DETAILS_CLASS}>
                        {t('tickets.showing-details')}
                    </button>
                ) : (
                    <Link
                        href={compactDetailsHref}
                        className={COMPACT_DETAILS_CLASS}>
                        {t('tickets.showing-details')}
                    </Link>
                )}
            </div>
        );
    }

    return (
        <div
            className={'flex flex-col space-y-6 mt-8 mb-12 lg:mx-[16px] lg:m-[16px] text-[var(--on-background-color)]'}>
            <div className="flex flex-row space-x-6 items-center text-body-bold-14">
                {showCinemaName && cinema && (
                    <>
                        <CinemaProfileImageRound
                            size="24"
                            cinema={cinema}
                        />
                        <div className="whitespace-nowrpap">{cinema.name}</div>
                    </>
                )}
                {showCinemaName && cinema && cinemaRoom && <span className="opacity-20 px-4">|</span>}
                {cinemaRoom && (
                    <div className={cn(!showCinemaName && 'opacity-70', 'whitespace-nowrap')}>{cinemaRoom.name}</div>
                )}
            </div>

            <div className="flex space-x-12">
                <div className={'relative w-64 h-96 rounded-4 overflow-clip flex-none'}>
                    <MoviePoster
                        movie={movie ?? undefined}
                        countryCode={countryCode}
                    />
                </div>
                <div>
                    <div className="flex flex-col space-y-6">
                        <h2 className="text-body-bold-14 lg:text-headline-18">{showingName}</h2>
                        <div className="text-body-12 lg:text-body-14 font-medium leading-[24px]">
                            {showingDateTime} <br />
                            <DotSeparatedLabel
                                labels={[languageInfo, ageRatingText].flatMap((it) => (it ? [it] : []))}
                            />
                        </div>
                    </div>

                    <div className="flex flex-wrap space-x-6 pt-6">
                        {showingProperties.map((property, index) => (
                            <div
                                key={`badge#${index}`}
                                className="px-4 text-body-bold-10 font-montserrat-bold rounded-2 leading-[16px] bg-[rgba(var(--primary-color-rgb),0.2)]">
                                <span className="text-[var(--primary-color)]">{property}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShowingDetails;
